'use client'

import { useEffect, useMemo, useState } from 'react'
import { Button, Dialog, Divider, Navigation, Spacer } from '@vinted/web-ui'
import { X24 } from '@vinted/monochrome-icons'

import {
  AddressModel,
  businessAccountFromUserDto,
  setBusinessAccountAddresses,
  viewScreenEvent,
} from '@marketplace-web/business-accounts/business-accounts-data'
import { ErrorItem } from '@marketplace-web/core-api/api-client-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useSession } from '@marketplace-web/shared/session-data'
import { AddressField } from '@marketplace-web/address/address-data'

import AddressForm from '../../../AddressForm'

type ReturnAddressModalProps = {
  businessAddress: AddressModel | null
  returnAddress: AddressModel | null
  isReturnAddressModalOpen: boolean
  onToggleReturnAddressModal: () => void
  onToggleUsingTheSameAddress: () => void
  onChangeReturnAddressErrorShow: (showError: boolean) => void
  onChangeReturnAddress: (address: AddressModel | null) => void
}

const ReturnAddressModal = ({
  businessAddress,
  returnAddress,
  isReturnAddressModalOpen,
  onToggleReturnAddressModal,
  onToggleUsingTheSameAddress,
  onChangeReturnAddressErrorShow,
  onChangeReturnAddress,
}: ReturnAddressModalProps) => {
  const { track } = useTracking()
  const businessAccountId = businessAccountFromUserDto(useSession().user)?.id as number
  const defaultValues = useMemo((): Partial<AddressModel> => {
    if (returnAddress) {
      return returnAddress
    }

    if (businessAddress) {
      return { countryId: businessAddress.countryId }
    }

    return {}
  }, [returnAddress, businessAddress])
  const [errors, setErrors] = useState<Array<ErrorItem>>([])
  const isDeleteVisible = !!returnAddress

  const translate = useTranslate('profile_settings.business_addresses')

  useEffect(() => {
    if (!isReturnAddressModalOpen) return

    track(viewScreenEvent({ screen: 'edit_return_address' }))
  }, [track, isReturnAddressModalOpen])

  function onCancel() {
    onToggleReturnAddressModal()
  }

  async function onDelete() {
    if (!businessAddress) return

    await setBusinessAccountAddresses({
      businessAccountId,
      businessAddress,
      returnAddress: null,
    })

    onToggleUsingTheSameAddress()
    onChangeReturnAddress(null)
    onCancel()
  }

  async function onSubmit(address: AddressModel, isFormValid: boolean) {
    if (!isFormValid) return
    const response = await setBusinessAccountAddresses({
      businessAccountId,
      returnAddress: address,
      businessAddress,
    })

    if ('errors' in response) {
      setErrors(response.errors)
      onChangeReturnAddressErrorShow(true)

      return
    }

    onChangeReturnAddress(address)

    onChangeReturnAddressErrorShow(false)
    onCancel()
  }

  function renderNavigation() {
    return (
      <Navigation
        theme="transparent"
        body={
          <>
            <Spacer size={Spacer.Size.Medium} />
            {translate('return_address.title')}
            <Spacer size={Spacer.Size.Medium} />
          </>
        }
        right={
          isDeleteVisible && (
            <Button styling={Button.Styling.Flat} onClick={onCancel} iconName={X24} />
          )
        }
      />
    )
  }

  const translations = {
    name: {
      title: translate('return_address.form_name.title'),
      placeholder: translate('return_address.form_name.placeholder'),
    },
    buttons: {
      continue: translate('buttons.submit'),
    },
  }

  const fieldConfiguration = [
    {
      field: AddressField.Country,
      disabled: true,
    },
    {
      field: AddressField.Name,
      required: true,
    },
    {
      field: AddressField.Line1,
      required: true,
    },
    {
      field: AddressField.Line2,
    },
    {
      field: AddressField.PostalCode,
      required: true,
    },
    {
      field: AddressField.City,
      required: true,
    },
  ]

  const renderAddressForm = () => (
    <AddressForm
      isSubmitPending={false}
      defaultValues={defaultValues}
      onSubmit={onSubmit}
      onCancel={onCancel}
      onDelete={onDelete}
      errors={errors}
      translations={translations}
      fieldConfiguration={fieldConfiguration}
      isDeleteVisible={isDeleteVisible}
    />
  )

  return (
    <Dialog show={isReturnAddressModalOpen} testId="address-modal">
      {renderNavigation()}
      <Divider />
      {renderAddressForm()}
    </Dialog>
  )
}

export default ReturnAddressModal
